import { useCallback, useMemo, useState } from 'react';
import { formatCalendarMonth } from '../../../utils/dateTimeUtils';
import {
  getMonthOverviewDateRange,
  getYearOverviewDateRange,
  sumTenantOverviewEstimatedCosts,
} from '../tenantOverviewUtils';
import {
  useLacTenantOverviewsQueries,
  useLacTenantOverviewsQuery,
} from '../queries/useLacTenantOverviewsQuery';

export type LacOverallCostsMonthCell = {
  /** 1–12 */
  month: number;
  /** `YYYY-MM` */
  key: string;
  cost: number;
  isFuture: boolean;
  isSelected: boolean;
};

function maxMonthForYear(year: number, now: Date): number {
  if (year < now.getFullYear()) {
    return 12;
  }
  if (year > now.getFullYear()) {
    return 0;
  }
  return now.getMonth() + 1;
}

export function useLacOverallCostsCard() {
  const now = useMemo(() => new Date(), []);
  const [isExpanded, setIsExpanded] = useState(false);
  const [year, setYear] = useState(() => now.getFullYear());
  const [month, setMonth] = useState(() => now.getMonth() + 1);

  const maxMonth = maxMonthForYear(year, now);
  const selectedMonth = Math.min(month, Math.max(1, maxMonth || 1));
  const queriesEnabled = isExpanded && maxMonth > 0;

  const yearRange = useMemo(
    () => getYearOverviewDateRange(year, now),
    [year, now],
  );
  const yearQuery = useLacTenantOverviewsQuery(yearRange, {
    enabled: queriesEnabled,
  });

  const monthEntries = useMemo(
    () =>
      Array.from({ length: maxMonth }, (_, index) => {
        const monthNumber = index + 1;
        return {
          month: monthNumber,
          key: formatCalendarMonth(year, monthNumber),
          range: getMonthOverviewDateRange(year, monthNumber, now),
        };
      }),
    [year, maxMonth, now],
  );

  const monthRanges = useMemo(
    () => monthEntries.map((entry) => entry.range),
    [monthEntries],
  );

  const monthQueries = useLacTenantOverviewsQueries(monthRanges, {
    enabled: queriesEnabled,
  });

  const costsByMonth = useMemo(() => {
    const map = new Map<string, number>();
    for (let index = 0; index < monthEntries.length; index += 1) {
      const entry = monthEntries[index];
      const query = monthQueries[index];
      map.set(entry.key, sumTenantOverviewEstimatedCosts(query?.data));
    }
    return map;
  }, [monthEntries, monthQueries]);

  const monthCells: LacOverallCostsMonthCell[] = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const monthNumber = index + 1;
      const key = formatCalendarMonth(year, monthNumber);
      const isFuture = monthNumber > maxMonth;
      return {
        month: monthNumber,
        key,
        cost: isFuture ? 0 : (costsByMonth.get(key) ?? 0),
        isFuture,
        isSelected: monthNumber === selectedMonth,
      };
    });
  }, [year, maxMonth, costsByMonth, selectedMonth]);

  const monthlyTotal =
    costsByMonth.get(formatCalendarMonth(year, selectedMonth)) ?? 0;
  const yearlyTotal = sumTenantOverviewEstimatedCosts(yearQuery.data);

  const canGoNextYear = year < now.getFullYear();
  const canGoNextMonth =
    year < now.getFullYear() ||
    (year === now.getFullYear() && selectedMonth < now.getMonth() + 1);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const goPrevYear = useCallback(() => {
    setYear((prev) => prev - 1);
  }, []);

  const goNextYear = useCallback(() => {
    setYear((prev) => Math.min(prev + 1, now.getFullYear()));
  }, [now]);

  const goPrevMonth = useCallback(() => {
    if (selectedMonth > 1) {
      setMonth(selectedMonth - 1);
      return;
    }
    setYear((prev) => prev - 1);
    setMonth(12);
  }, [selectedMonth]);

  const goNextMonth = useCallback(() => {
    if (!canGoNextMonth) {
      return;
    }
    if (selectedMonth < 12) {
      setMonth(selectedMonth + 1);
      return;
    }
    setYear((prev) => Math.min(prev + 1, now.getFullYear()));
    setMonth(1);
  }, [canGoNextMonth, selectedMonth, now]);

  const selectMonth = useCallback(
    (monthNumber: number) => {
      if (monthNumber < 1 || monthNumber > maxMonth) {
        return;
      }
      setMonth(monthNumber);
    },
    [maxMonth],
  );

  const isLoading =
    yearQuery.isLoading || monthQueries.some((query) => query.isLoading);
  const isError =
    yearQuery.isError || monthQueries.some((query) => query.isError);

  return {
    isExpanded,
    toggleExpanded,
    year,
    selectedMonth,
    monthCells,
    monthlyTotal,
    yearlyTotal,
    canGoNextYear,
    canGoNextMonth,
    goPrevYear,
    goNextYear,
    goPrevMonth,
    goNextMonth,
    selectMonth,
    isLoading,
    isError,
  };
}
